// src/pages/sqf/SqfCorrectiveActionsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const displayName = (u) => (u?.firstName || u?.lastName) ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : (u?.username || '—');

const STATUS_BADGE = {
  open:     { label: 'Open',     color: 'danger'  },
  resolved: { label: 'Resolved', color: 'warning' },
  verified: { label: 'Verified', color: 'success' },
};

export default function SqfCorrectiveActionsPage() {
  const { lang } = useLanguage();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');
  const [expanded, setExpanded] = useState(null);

  const load = (status) => {
    setLoading(true);
    sqfService.getCorrectiveActions(status ? { status } : {})
      .then(setActions)
      .catch(() => setError(t('Failed to load corrective actions.', 'Error al cargar las acciones correctivas.', lang)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filterStatus); }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResolve = async (id) => {
    try {
      await sqfService.resolveCorrectiveAction(id);
      load(filterStatus);
    } catch { setError(t('Failed to resolve.', 'Error al resolver.', lang)); }
  };

  const handleVerify = async (id) => {
    try {
      await sqfService.verifyCorrectiveAction(id);
      load(filterStatus);
    } catch { setError(t('Failed to verify.', 'Error al verificar.', lang)); }
  };

  const statusLabel = (key) => {
    const map = {
      open:     t('Open',     'Abierto',   lang),
      resolved: t('Resolved', 'Resuelto',  lang),
      verified: t('Verified', 'Verificado', lang),
    };
    return map[key] || key;
  };

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <Link to="/sqf" className="text-muted" style={{ fontSize: 12 }}>← {t('Dashboard', 'Panel', lang)}</Link>
          <h4 className="mb-0 mt-1">{t('Corrective Actions', 'Acciones correctivas', lang)}</h4>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {/* Filter tabs */}
      <ul className="nav nav-tabs mb-3">
        {[['open', t('Open', 'Abierto', lang)], ['resolved', t('Resolved', 'Resuelto', lang)], ['verified', t('Verified', 'Verificado', lang)], ['', t('All', 'Todos', lang)]].map(([val, label]) => (
          <li className="nav-item" key={val}>
            <button className={`nav-link ${filterStatus === val ? 'active' : ''}`} onClick={() => setFilterStatus(val)}>{label}</button>
          </li>
        ))}
      </ul>

      {loading ? (
        <div className="text-center py-5 text-muted"><div className="spinner-border spinner-border-sm me-2" />{t('Loading…', 'Cargando…', lang)}</div>
      ) : actions.length === 0 ? (
        <div className="text-center py-5 text-muted">{t('No corrective actions found.', 'No se encontraron acciones correctivas.', lang)}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover table-bordered">
            <thead className="table-light">
              <tr>
                <th>{t('Checklist', 'Lista de verificación', lang)}</th>
                <th>{t('Date', 'Fecha', lang)}</th>
                <th>{t('Description', 'Descripción', lang)}</th>
                <th>{t('Status', 'Estado', lang)}</th>
                <th>{t('Created By', 'Creado por', lang)}</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {actions.map(ca => {
                const logInst = ca.Log?.Instance;
                const tmpl = logInst?.Template;
                const badge = STATUS_BADGE[ca.status] || { label: ca.status, color: 'secondary' };
                return (
                  <React.Fragment key={ca.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(expanded === ca.id ? null : ca.id)}>
                      <td className="fw-semibold" style={{ fontSize: 13 }}>{tmpl?.name || '—'}</td>
                      <td style={{ fontSize: 12 }}>{ca.Log?.submittedAt ? new Date(ca.Log.submittedAt).toLocaleDateString() : '—'}</td>
                      <td style={{ fontSize: 13, maxWidth: 300 }}><span className="text-truncate d-block" style={{ maxWidth: 280 }}>{ca.description}</span></td>
                      <td><span className={`badge bg-${badge.color}`}>{statusLabel(ca.status)}</span></td>
                      <td style={{ fontSize: 12 }}>{ca.CreatedBy ? displayName(ca.CreatedBy) : '—'}</td>
                      <td className="text-end" onClick={e => e.stopPropagation()}>
                        {ca.Log && <Link to={`/sqf/logs/${ca.Log.id}`} className="btn btn-outline-secondary btn-sm me-1">{t('Log', 'Registro', lang)}</Link>}
                        {ca.status === 'open' && <button className="btn btn-outline-success btn-sm" onClick={() => handleResolve(ca.id)}>{t('Resolve', 'Resolver', lang)}</button>}
                        {ca.status === 'resolved' && <button className="btn btn-outline-primary btn-sm" onClick={() => handleVerify(ca.id)}>{t('Verify', 'Verificar', lang)}</button>}
                      </td>
                    </tr>
                    {expanded === ca.id && (
                      <tr>
                        <td colSpan={6} className="bg-light">
                          <div className="px-2 py-1" style={{ fontSize: 13 }}>
                            {ca.rootCause && <div><strong>{t('Root cause:', 'Causa raíz:', lang)}</strong> {ca.rootCause}</div>}
                            {ca.preventativeMeasure && <div><strong>{t('Preventative measure:', 'Medida preventiva:', lang)}</strong> {ca.preventativeMeasure}</div>}
                            {ca.resolvedAt && <div className="text-muted">{t('Resolved:', 'Resuelto:', lang)} {new Date(ca.resolvedAt).toLocaleString()} {t('by', 'por', lang)} {ca.ResolvedBy ? displayName(ca.ResolvedBy) : '—'}</div>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
}
